/**
 * @license
 * ©2018-2019 EdgeVerve Systems Limited (a fully owned Infosys subsidiary),
 * Bangalore, India. All Rights Reserved.
 */

import { html, PolymerElement } from "@polymer/polymer/polymer-element.js";
import { OECommonMixin } from "oe-mixins/oe-common-mixin.js";
import "@polymer/iron-flex-layout/iron-flex-layout-classes.js";
import "@polymer/paper-dropdown-menu/paper-dropdown-menu.js";
import "@polymer/paper-listbox/paper-listbox.js";
import "@polymer/paper-item/paper-item.js";
import "@polymer/paper-icon-button/paper-icon-button.js";
import "@polymer/iron-icons/image-icons.js";

/**
 * `oe-data-table-pagination-panel`
 *  Pagination panel used in oe-data-table
 * 
 * ### Styling
 * 
 * The following custom properties and mixins are available for styling:
 * 
 * CSS Variable | Description | Default
 * ----------------|-------------|----------
 * `--oe-pagination-panel` | Mixin applied on the entire panel | {}
 * 
 * 
 * @customElement
 * @polymer
 * @appliesMixin OECommonMixin
 */
class OeDataTablePaginationPanel extends OECommonMixin(PolymerElement) {

  static get is() { return 'oe-data-table-pagination-panel'; }

  static get template() {
    return html`
    <style include="iron-flex">
    
      .pagination-panel {
          width: 100%;
          height: 56px;
          font-size: 12px;
          font-weight: 600;
          padding: 0 14px;
          box-sizing: border-box;
          color: rgba(0, 0, 0, 0.54);
          -webkit-user-select: none;
          border-top: 1px solid #ededed;
    
          @apply --layout-horizontal;
          @apply --layout-center;
          @apply --layout-end-justified;
          @apply --oe-pagination-panel
        }
    
        .row-details {
          padding: 0 32px;
        }
    
        .pagination-dropdown-title {
          margin-left: 32px;
        }
    
        paper-dropdown-menu {
          padding: 0 10px;
          padding-top: 2px;
    
          --paper-dropdown-menu-input: {
            width: 50px;
          }
          --paper-input-container-underline: {
            display: none;
          }
          --paper-input-container-input: {
            font-size: 12px;
            text-align: center;
          }
        }
              .pagination-panel {
          margin: 42px auto;
          @apply --layout-center-center;
        }
        .pagination{
          width: 45px !important;
          height: 45px !important;
          border-left: solid 1px white;
          border-right: solid 1px white;
          background-color: white;
        }
        .pagination:hover {
          color: black;
          background: lightblue;
          cursor: pointer;
		  border-top: solid 1px grey;
		  border-bottom: solid 1px grey;
          border-left: solid 1px grey;
          border-right: solid 1px grey;
          transition: all ease 0.3s;
        }
        .pagination[selected]{
          color: blue;
          background: lightblue;
          border-left: solid 1px white;
          border-right: solid 1px white;
        }
        .paginationDiv{
		  max-width: 100%;
          border: solid 1px var(--blue-100);
          @apply --layout-horizontal;
        }
        paper-icon-button[disabled] {
          opacity: 0.38;
        }
    </style>
    <div class="pagination-panel" hidden="[[_computeHidden(rowCount, pageSize)]]">
        <div class="paginationDiv">
          <paper-icon-button class="pagination text-blue" disabled$="[[_disablePrevious(currentPage)]]" icon="image:navigate-before" on-tap="_previousPage"></paper-icon-button>
          <template is="dom-repeat" items="[[_getPages(rowCount, pageSize)]]">
            <div class="pagination text-1 font-weight-500 horizontal layout center-center" hidden$="{{_showPaginationBox(item,currentPage)}}" on-tap="_selectPage" selected$={{_checkSelected(item,currentPage)}}>
              [[item]]
            </div>
                </template>
          <paper-icon-button class="pagination text-blue" disabled$="[[_isLastPage]]" icon="image:navigate-next" on-tap="_nextPage"> </paper-icon-button>
        </div>
    </div>
    `;
  }

  static get properties() {
    return {
      /**
       * page size value
       */
      pageSize: {
        type: Number,
        notify: true
      },
      /**
       * Array containing different page sizes
       */
      rowsPerPageItems: {
        type: Array
      },

      /**
       * Current page index
       */
      currentPage:{
          type:Number,
          notify:true
      },

      /**
       * Length of items in this page
       */
      itemsLength:{
          type:Number
      },

      /**
       * Total count of items
       */
      rowCount:{
        type:Number
      }
    };
  }

  static get observers() {
    return ['_computeRowIndex(currentPage,pageSize,itemsLength,rowCount)'];
  }

  /**
   * Computes the value for '_startRow' and '_endRow'
   * @param {number} currentPage current page number
   * @param {number} pageSize total page size
   * @param {number} itemsLength length of items displayed
   * @param {number} rowCount total number of rows in data.
   */
  _computeRowIndex(currentPage,pageSize,itemsLength,rowCount) {
        var startIndex = (currentPage - 1) * pageSize;
        this.set('_startRow',startIndex+1);
        this.set('_endRow', startIndex + itemsLength);
        this.set('_isLastPage',this._endRow == rowCount);
  }

  /**
   * Computes total row count display
   * @param {number} itemsLength length of items displayed
   * @return {string|number} total rows
   */
  _totalRowsCount(itemsLength){
    return itemsLength == Infinity ? 'More' : itemsLength;
  }

  /**
   * Checks if the previous button should be disabled
   * @param {number} currentPage current page index
   * @return {boolean} flag to disable the previous page button
   */
  _disablePrevious(currentPage){
    return currentPage && currentPage == 1;
  }
 
  /**
   * Navigates to previous page
   * @param {Event} event HTML click event
   */
  _previousPage(event) { // eslint-disable-line no-unused-vars
    this.async(function () {
      this.set('currentPage', this.currentPage - 1);
    });
  }

  /**
   * Navigates to next page
   * @param {Event} event  HTML click event
   */
  _nextPage(event) { // eslint-disable-line no-unused-vars
    this.async(function () {
      this.set('currentPage', this.currentPage + 1);
    });
  }

  _selectPage(event){
    var pageNumber = event.model.__data.item;
    this.async(function () {
      this.set('currentPage', pageNumber);
    });
  }
  _getPages(rowCount, pageSize){
    var renderArr = [];
    var division = rowCount/pageSize;
    var remainder = rowCount%pageSize;
    if(remainder > 0)
      division = division + 1;
    for(var i=1; i<=division; i++){
      renderArr.push(i);
    }
    this.numberOfPagesLength = renderArr.length;
    return renderArr;
  }
  _checkSelected(item,currentPage) {
    if(item === currentPage)
      return true;
    return false;
  }
  _showPaginationBox(item, currentPage){
    var index = 2;
    if(currentPage === 1  || currentPage === this.numberOfPagesLength)
      index = 3;
    if(item > (currentPage - index) && item <= currentPage)
      return false;
    else if(item < (currentPage + index) && item >= currentPage)
      return false;
    return true;
  }
  _computeHidden(rowCount, pageSize) {
    if (rowCount > pageSize) return false;
    else return true;
  }
}

window.customElements.define(OeDataTablePaginationPanel.is, OeDataTablePaginationPanel);